import { FC, useState, useEffect, FormEvent, ChangeEventHandler, useRef } from "react";
import {ModalBody} from "@/widgets/ModalParts";
import { useDispatch, useSelector } from "react-redux";
import { IoMdClose } from "react-icons/io";
import { FaApple, FaGoogle, FaDiscord } from "react-icons/fa";
import {Modal} from "@/widgets/Modal";
import {Button} from "@/components/buttons/Button";
import Checkbox from "@/components/inputs/Checkbox";
import AssetImage from "@/components/AssetImage";
import { useGetAsset } from "@/hooks/useAsset";
import { IStore } from "@/redux/defaultStore";
import { addError, decrementLoading, incrementLoading } from "@/redux/meta/metaActions";
import { Challenge, VerificationsApi } from "@devour/client";
import { useGetGeography } from "@/hooks/useGetGeography";
import { SearchableSelect } from "@/components/inputs/SearchableSelect";
import { validateEmail } from "@/utils/validateEmail";
import getConfig from "@/utils/getConfig";
import LoginVerificationCode from "@/components/login/LoginVerificationCode";
import useOnAuthThirdweb from "@/hooks/useOnAuthThirdweb";
import { useCreateChallengeRealmSignin } from "@/hooks/useCreateChallengeRealmSignin";
import { useCreateDropRealmSignin } from "@/hooks/drop/useCreateDropRealmSignin";
import {getAuthorizedExternalOrigin} from "@/components/auth/AuthLoginRedirect";
import {useSearchParams} from "react-router";
import {useGetAcquisitionSignals} from "@/hooks/useGetAcquisitionSignals";

/**
 * Payload handed to onLoginSuccess once the user signs in. eligibility carries any
 * per-challenge attestation checkbox values; declaredCountry / declaredState carry
 * the geo declaration captured when the challenge has Challenge.eligibilityGeo set
 * (omitted/undefined for challenges without a geo gate).
 */
export interface ChallengeLoginSuccessData {
    eligibility?: Record<string, boolean>;
    declaredCountry?: string;
    declaredState?: string;
}

export interface ChallengeLoginModalProps {
    challenge?: Challenge;
    isOpen: boolean;
    onClose: () => void;
    /**
     * Called after successful sign-in. Carries the attestation + geo values the
     * modal collected so the parent can post them to /challenges/:slug/register.
     */
    onLoginSuccess?: (data?: ChallengeLoginSuccessData) => void;
    title?: string;
    description?: string;
    referralCode?: string;
    errorMessage?: string;
    /**
     * Explicit challenge slug passed by the parent hub. Takes precedence over challenge.slug
     * for the login-code request so the bundler cannot optimize it away.
     */
    challengeSlug?: string;
    /** Drop-door branding: sends dropSlug so the code + welcome emails use the Drop's onboarding experience templates. */
    dropSlug?: string;
    /**
     * Member-status lookup from the platform-neutral
     * `/challenges/:slug/member-status` endpoint. When the backend
     * already has attestation / declared geo on file for the current
     * partner-platform visitor, the modal hides those blocks so the
     * user only enters email + verification code. When `canCodeless` is set,
     * the modal skips the code entirely via the codeless "Continue" path — see
     * the canCodeless/realmCountry fields below. Undefined (or
     * `hasMembership=false` with no `canCodeless`) → full attestation + geo
     * flow with email + code.
     */
    memberStatus?: {
        hasMembership: boolean;
        attestationOnFile: boolean;
        countryOnFile: boolean;
        stateRequired: boolean;
        /*
         * Single-stage (codeless) signal. `canCodeless` true → "Continue" signs
         * the user in off the signed embed with NO verification code (POST
         * /challenges/:slug/realm-signin). Always true for an embed visitor;
         * absent for non-embed web visitors → the standard email + code flow.
         */
        canCodeless?: boolean;
    };
    /**
     * Realm-provided country of residence (full name, e.g. "United States") used
     * to pre-select the Country picker. Fetched ASYNC (separate from member-status)
     * so it never blocks the modal — arrives a beat after the modal opens. Null /
     * undefined = nothing to pre-fill. The user still confirms it.
     */
    realmCountry?: string | null;
    /**
     * True while the async realmCountry fetch is in flight — the Country picker
     * shows a skeleton in place of the field until it resolves (or the user picks).
     */
    realmCountryLoading?: boolean;
    /**
     * Modal is open but its final shape (challenge + member-status) hasn't resolved
     * yet — show a loader instead of the form so the hub can pop the modal
     * IMMEDIATELY on load without flashing the generic social/email buttons before
     * the codeless state lands. Defaults false (other callers render right away).
     */
    isLoading?: boolean;
    /**
     * Render the geo (Country/State) block BEFORE the attestation checkboxes and
     * unlock the pickers from the start (RTTR, client 2026-07-28). Default false
     * keeps the original order for every other consumer (Polaris, generic
     * ChallengePage, MissionsSection): checkboxes first, geo second, pickers locked
     * until the attestations are ticked. Continue is gated on BOTH either way.
     */
    geoFirst?: boolean;
    /*
     * WHO CAN WIN — a separate question from whether we collect geo.
     *
     * `geoFirst` used to answer both: it forced the Country/State section on
     * AND turned on the US-only prize framing (the mandatory "only U.S.
     * residents are eligible to win prizes" acknowledgement and the
     * bragging-rights notice). Race to the Realm needs both. Road to the
     * Finals needs only the first — it resets weekly, nobody advances to a
     * US Finals seat off it, and it is open worldwide, so telling a joiner
     * they cannot win is simply false.
     *
     * UNSET keeps the legacy behaviour exactly: the framing follows the
     * challenge's own `eligibilityGeo.admitAllCountries`, which is how every
     * pre-existing consumer already decided it. Only the two Minelab surfaces
     * declare it, because only they are affected by `geoFirst` forcing
     * `admitAllCountries` true regardless of what the challenge says.
     */
    prizeEligibility?: "US_ONLY" | "OPEN";
}

enum LoginState {
    INITIAL,
    EMAIL_VERIFY,
}

interface SocialProvider {
    id: string;
    name: string;
    icon: React.ReactNode;
    href: string;
}

const socialProviders: SocialProvider[] = [
    {
        id: "discord",
        name: "Discord",
        icon: <FaDiscord />,
        href: `${import.meta.env.VITE_BACKEND_REST_URL}/passport/discord/login`,
    },
    {
        id: "google",
        name: "Google",
        icon: <FaGoogle />,
        href: `${import.meta.env.VITE_BACKEND_REST_URL}/passport/google/login`,
    },
    {
        id: "apple",
        name: "Apple",
        icon: <FaApple />,
        href: `${import.meta.env.VITE_BACKEND_REST_URL}/passport/apple/login`,
    },
];

export const ChallengeLoginModal: FC<ChallengeLoginModalProps> = ({
    challenge,
    isOpen,
    onClose,
    onLoginSuccess,
    title = "Sign in to continue",
    description = "To track your challenge progress, you will need a DevourPlay account. Sign-up or Login below.",
    errorMessage,
    challengeSlug: challengeSlugProp,
    dropSlug,
    memberStatus,
    isLoading = false,
    geoFirst = false,
    prizeEligibility,
    realmCountry,
    realmCountryLoading = false,
}) => {
    /*
     * Touch devices only: the Country/State pickers open on tap WITHOUT raising
     * the on-screen keyboard (see SearchableSelect `noKeyboard`). On iOS an
     * editable text input raises the keyboard, which shrinks the visual viewport
     * and reflows/grows the fixed modal ("modal explodes when I select a state").
     * Scoped to the geoFirst (RTTR) pickers so no other consumer changes.
     */
    const isTouchDevice = typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(pointer: coarse)").matches;
    /*
     * When the backend memberStatus says attestation / country are already on
     * file for the embedded Realm user, hide those blocks AND short-
     * circuit their gating booleans so the email field unlocks
     * immediately. `stateRequired` is the narrow corner case where
     * country=US is recorded but state is not — surface only the state
     * picker in that case. New / partial Realm users (and any non-
     * embed web visitor) get `memberStatus = undefined` and the full attestation + geo flow.
     */
    const hideAttestation = Boolean(memberStatus?.hasMembership && memberStatus?.attestationOnFile);
    const hideCountry = Boolean(memberStatus?.hasMembership && memberStatus?.countryOnFile && !memberStatus?.stateRequired);
    // Prefer the explicit prop so the hub can pass a string literal the bundler cannot optimize away.
    const resolvedChallengeSlug = challengeSlugProp ?? challenge?.slug;
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const referralCodeQuery = searchParams.get("ref");
    const acquisition = useGetAcquisitionSignals();
    const { fullToken } = useSelector((store: IStore) => store.authStore);
    const { data: background } = useGetAsset(challenge?.backgroundImageLarge);
    const { onReauthorize, onProfileRegistration } = useOnAuthThirdweb();
    const realmSignin = useCreateChallengeRealmSignin();
    const dropRealmSignin = useCreateDropRealmSignin();
    /*
     * A Drop has no challenge, so it has no challenge slug — the codeless path
     * must key on the door it is actually standing at or its Continue guards
     * itself off and the button does nothing.
     */
    const codelessDoor: { kind: "drop"; slug: string } | { kind: "challenge"; slug: string } | undefined =
        dropSlug
            ? {kind: "drop", slug: dropSlug}
            : resolvedChallengeSlug ? {kind: "challenge", slug: resolvedChallengeSlug} : undefined;
    const isSubmittingRef = useRef(false);
    /*
     * Codeless path fell through to the standard email + code flow. Set when the
     * visitor was codeless-eligible (canCodeless) but the realm-signin call came
     * back 422 (no email on file — race / propagation lag on a new signup).
     * Overrides showCodeless so the user sees the email field instead of a dead
     * end. (A returning member can't hit this — they resolve realmUuid-first,
     * never via email — but the fallback is harmless if it ever fires.)
     */
    const [forceEmailFallback, setForceEmailFallback] = useState(false);
    const reduxReferralCode = useSelector((store: IStore) => store.affiliateStore.referralCode);
    const reduxReferralExpiry = useSelector((store: IStore) => store.affiliateStore.referralExpiry);
    const reduxReferralValid = reduxReferralCode && Date.now() < reduxReferralExpiry ? reduxReferralCode : "";

    const [loginState, setLoginState] = useState<LoginState>(LoginState.INITIAL);
    const [isOver18, setIsOver18] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [extraEligibility, setExtraEligibility] = useState<Record<string, boolean>>({});
    const [email, setEmail] = useState("");
    const [manualReferralCode, setManualReferralCode] = useState("");
    const [loginRequestId, setLoginRequestId] = useState("");
    const [hasVerificationCodeError, setHasVerificationCodeError] = useState(false);
    const [isEmailFocused, setIsEmailFocused] = useState(false);
    const [isEmailHovered, setIsEmailHovered] = useState(false);
    /*
     * Per-field focus tracking for the geo + referral inputs. We mirror
     * the email-input pattern (inline borderColor on focus) because the
     * global `select:focus` / `input:focus` rule in style/inputs/_input.scss
     * wraps its border-color in `@include dark-mode` and compiles to
     * a higher-specificity selector than anything class-scoped. Inline
     * styles trump every CSS rule (without `!important`), so this is the
     * only reliable path to the brand-color border on focus.
     */
    const [isReferralFocused, setIsReferralFocused] = useState(false);

    /*
     * Geo declaration state — only used when the geo gate is active.
     * Country defaults to empty so the user explicitly picks "Select Country"
     * (no implicit US assumption — a non-US user should not have to override
     * a default to declare correctly). Whether non-allowed countries are
     * admitted (bragging-rights soft_block path) or 403'd is decided by
     * Challenge.eligibilityGeo.admitAllCountries on the backend.
     */
    const eligibilityGeo = challenge?.eligibilityGeo;
    const hasExplicitGeoGate = !!(eligibilityGeo && (eligibilityGeo.allowedCountries?.length || eligibilityGeo.hardBlockStates?.length || eligibilityGeo.softBlockStates?.length || eligibilityGeo.hardBlockTerritories || eligibilityGeo.admitAllCountries));
    /*
     * Surface gate for the universal prize-eligibility checkbox + Country
     * picker (and conditional State picker). Renders when EITHER the
     * challenge has eligibilityGeo configured OR carries a branded
     * referral-code prefix. The referral-code-prefix opt-in keeps the
     * branded sweepstakes flow rendering even when eligibilityGeo hasn't
     * been populated on the underlying Challenge document yet — otherwise
     * an unpopulated document would silently drop both the checkbox and
     * the country picker, leaving the modal in its generic form.
     */
    /*
     * geoFirst (RTTR) ALWAYS has the geo gate — force it true so the Country/State
     * section renders immediately, before the `challenge` fetch resolves. Otherwise
     * hasGeoGate depends on `challenge` and the section silently vanishes during the
     * cold load window (only the terms box showed — the "midget" regression).
     */
    const hasGeoGate = geoFirst || hasExplicitGeoGate || !!challenge?.referralCodePrefix;
    /*
     * admitAllCountries === true → registration admits non-allowed countries
     * with status="prize-restricted" instead of 403. Defaults true for any
     * branded-prefix challenge so a locally-served Challenge document still
     * follows the bragging-rights admission policy without requiring the
     * eligibilityGeo field to be populated on every record.
     */
    const admitAllCountries = geoFirst ? true : eligibilityGeo?.admitAllCountries ?? !!challenge?.referralCodePrefix;
    /*
     * The US-only prize framing — a different question from who may JOIN.
     *
     * A surface that declares wins. Unset falls back to the CHALLENGE's own
     * `admitAllCountries`, never to the geoFirst-forced value above: geoFirst
     * forces that flag true to keep the Country/State pickers mounted during
     * the cold-load window, and reading prize policy off it is what put Race
     * to the Realm's sweepstakes copy on Road to the Finals.
     */
    const usOnlyPrizes = prizeEligibility === undefined
        ? eligibilityGeo?.admitAllCountries ?? false
        : prizeEligibility === "US_ONLY";
    const [declaredCountry, setDeclaredCountry] = useState<string>("");
    /*
     * Universal prize-eligibility acknowledgement. Required for every
     * registrant on any challenge that admits all countries — a NY/FL
     * resident, a non-US global user, and a CA-eligible US user all tick
     * the same checkbox, which states the program rule rather than the
     * user's own residence. Hardcoded inline because the copy is the same
     * across every admit-all-countries challenge; per-challenge custom
     * acknowledgements can still ship via Challenge.eligibilityCheckboxes.
     */
    const [prizeEligibilityAck, setPrizeEligibilityAck] = useState(false);
    const [declaredState, setDeclaredState] = useState<string>("");
    const { data: geography } = useGetGeography();

    /*
     * U.S. territory codes — mirror of the backend list in
     * post_challenge_slug_register.ts. Inline (vs a shared util) because the only
     * other consumer would be the address form, and that doesn't gate on
     * territories. Lift if a second consumer appears.
     */
    const US_TERRITORY_CODES = ["PR", "GU", "VI", "AS", "MP", "UM"];

    const geoCountry = declaredCountry.trim().toUpperCase();
    const geoState = declaredState.trim().toUpperCase();
    /*
     * Only flip true AFTER the user actually picks a country. Without the
     * `geoCountry.length > 0` guard, an empty `geoCountry=""` never
     * matches `allowedCountries: ["US"]` and the bragging-rights banner
     * renders the moment the modal opens — before any selection.
     */
    const isNonAllowedCountry = hasGeoGate &&
        geoCountry.length > 0 &&
        (eligibilityGeo?.allowedCountries?.length ?? 0) > 0 &&
        !eligibilityGeo?.allowedCountries?.includes(geoCountry);
    const isHardBlockedState = hasGeoGate && geoCountry === "US" && (
        (eligibilityGeo?.hardBlockStates?.includes(geoState) ?? false) ||
        !!eligibilityGeo?.hardBlockTerritories && US_TERRITORY_CODES.includes(geoState)
    );
    const isSoftBlockedState = hasGeoGate && geoCountry === "US" && (eligibilityGeo?.softBlockStates?.includes(geoState) ?? false);
    /*
     * Geo gate computed as a small IIFE so each "fails for reason X" line reads
     * top-to-bottom without an `&&`-chain that the formatter and stylistic-semi
     * eslint rule disagree on. Order matches user-facing UX precedence:
     * non-allowed country first (unless admitAllCountries=true → user joins for
     * bragging rights), then hard-block, then state-required. Soft-block states
     * (NY/FL) no longer require a separate acknowledgement gate — the universal
     * prize-eligibility checkbox above ("I understand only U.S. residents are
     * eligible to win prizes…") covers the legal acknowledgement for every
     * registrant, so a per-state ack is redundant. The informational banner
     * below still tells the user their specific status.
     */
    const geoFieldsValid = ((): boolean => {
        if (!hasGeoGate) return true;
        /*
         * Realm memberStatus says country (and state, if applicable) is
         * already on file — treat the geo block as satisfied so the
         * user doesn't have to re-pick what we already know.
         */
        if (hideCountry) return true;
        // Country defaults to empty so user must explicitly pick "Select Country".
        if (geoCountry.length === 0) return false;
        if (isNonAllowedCountry && !admitAllCountries) return false;
        if (isHardBlockedState) return false;
        if (geoCountry === "US" && geoState.length === 0) return false;
        return true;
    })();

    /*
     * Final referral code applied at signup — manual input wins when present
     * (user explicitly typed it), otherwise fall back to the URL ?ref= or
     * the persisted Redux affiliate code, in that order. Polaris CoD never
     * shows the manual input, so manualReferralCode stays empty there and
     * behavior is identical to before this change.
     */
    const referralCode = manualReferralCode.trim() || reduxReferralValid || referralCodeQuery || "";

    // Manual code entry only renders when the challenge is configured with a brand prefix
    /*
     * Referral code field is DROPPED for RTTR (geoFirst) — referrals are email
     * invites now, not a code entered here, and it must not appear in the email
     * fallback view either (the codeless view never showed it, and the two views
     * must look identical to the user). Other consumers keep the field.
     */
    const showManualReferralEntry = !geoFirst && !!challenge?.referralCodePrefix;

    const eligibilityCheckboxes = challenge?.eligibilityCheckboxes ?? [];
    const allRequiredExtraChecked = eligibilityCheckboxes
        .filter(b => b.required)
        .every(b => extraEligibility[b.key] === true);

    /*
     * Country + State pickers stay locked until the user attests to all
     * three universal acknowledgements (prize-eligibility / 18+ / Terms).
     * Prevents the form from collecting a declared jurisdiction before
     * the user has read what they're signing up to. Mirrors the pattern
     * the design specifies — attestation first, then declaration.
     * When the backend memberStatus says attestations are on file already
     * (returning Realm user), short-circuit to true so geo / email
     * unlock immediately.
     */
    const needsPrizeAck = usOnlyPrizes && admitAllCountries;
    const attestationsComplete = hideAttestation ||
        (!needsPrizeAck || prizeEligibilityAck) && isOver18 && agreedToTerms && allRequiredExtraChecked;

    /*
     * Single-stage codeless path: "Continue" authenticates off the signed embed
     * with no verification code. `canCodeless` is set for a returning member
     * (resolved realmUuid-first) OR a new visitor with a verified Realm email.
     * Only ever set for RTTR-style partner embeds (unset for every non-embed web
     * visitor), so this branch never affects other flows. Falls back to the
     * email path if the realm-signin call 422s.
     */
    /*
     * Codeless ONLY when member-status truthfully says so. member-status now
     * resolves canCodeless honestly (checks whether Realm actually returns an
     * email), and the hub's boot gate holds the modal until it resolves — so by the
     * time we render, this is the real decision, made UPFRONT. No `expectCodeless`
     * optimism: if member-status is missing (errored / timed out) we fall to the
     * email path, which always works, instead of showing a Continue that would fail.
     * forceEmailFallback covers a rare runtime codeless failure (e.g. an expired
     * handshake) by silently swapping to email — no banner, the user never sees it.
     */
    const showCodeless = Boolean(memberStatus?.canCodeless) && !forceEmailFallback;

    const areButtonsActive = (hideAttestation || isOver18 && agreedToTerms && allRequiredExtraChecked) &&
        geoFieldsValid &&
        (needsPrizeAck && !hideAttestation ? prizeEligibilityAck : true);

    /*
     * Optional per-challenge whitelist of allowed signup methods. Empty / undefined
     * preserves legacy behavior (all providers rendered). Cast to any pending
     * @devour/client regen — Challenge type doesn't yet include the new field.
     */
    const allowedSignupMethods: Array<string> = (challenge as any)?.allowedSignupMethods ?? [];
    const restrictMethods = allowedSignupMethods.length > 0;
    const visibleSocialProviders = restrictMethods
        ? socialProviders.filter(p => allowedSignupMethods.includes(p.id.toUpperCase()))
        : socialProviders;
    const showEmailMethod = !restrictMethods || allowedSignupMethods.includes("EMAIL");
    const showDividerBetweenMethods = visibleSocialProviders.length > 0 && showEmailMethod;

    // Track if we're doing email login (to avoid double-closing)
    const isEmailLoginRef = useRef(false);

    /*
     * The window message listener is registered once with empty deps, so closures
     * inside it would otherwise capture the initial state. Mirror extraEligibility
     * + geo into a ref so the social-login path reads the current values when
     * the popup posts back, rather than the stale {} from first render.
     */
    const successDataRef = useRef<ChallengeLoginSuccessData>({});
    useEffect(() => {
        successDataRef.current = {
            eligibility: extraEligibility,
            declaredCountry: hasGeoGate ? geoCountry : undefined,
            declaredState: hasGeoGate && geoCountry === "US" ? geoState : undefined,
        };
    }, [extraEligibility, hasGeoGate, geoCountry, geoState]);

    /*
     * Same staleness problem for referralCode — derived value that can change mid-session
     * (manual entry, ?ref= URL change). Without the ref, the social-registration popup
     * callback silently submits the empty/initial value instead of the typed one.
     */
    const referralCodeRef = useRef<string>("");
    useEffect(() => {
        referralCodeRef.current = referralCode;
    }, [referralCode]);

    /*
     * Record whether a token already existed at the moment the modal opened.
     * A returning user whose stored token is stale/expired (every authed call
     * 400s, but the token string is still in the store) would otherwise hit the
     * close effect below the instant the modal mounts — the modal "pops, flashes
     * and closes" and they can never re-authenticate. Declared before the close
     * effect so the ref is current when that effect reads it on the same commit.
     */
    const hadTokenOnOpenRef = useRef(false);
    useEffect(() => {
        if (isOpen) {
            hadTokenOnOpenRef.current = !!fullToken;
            /*
             * Reset the in-flight guard on every OPEN. handleContinueCodeless /
             * handleSubmitVerificationCode intentionally leave isSubmittingRef=true
             * on SUCCESS (the modal is closing; this blocks the ~120ms iOS/WKWebView
             * synthesized-click re-fire). But the modal is never unmounted, so a
             * subsequent reopen — e.g. the realmUuid-conflict 409 path logs the user
             * out and reopens this same instance asking them to sign in to the bound
             * account — would find isSubmittingRef stuck true and dead-click every
             * retry. Resetting on OPEN (not close) clears it for the retry while
             * keeping the close-window double-tap guard intact.
             */
            isSubmittingRef.current = false;
        }
    }, [isOpen]);

    /*
     * Close modal when user logs in via SOCIAL (popup flow).
     * Email login handles close explicitly in handleSubmitVerificationCode.
     *
     * Two guards, both required:
     *   - geoFieldsValid: on a geo-gated challenge a previously-authenticated
     *     user would otherwise fire onLoginSuccess with empty declaredCountry/
     *     declaredState before picking a state (parent register 400s). Hold
     *     until geo selection completes. Non-geo-gated challenges short-circuit
     *     (geoFieldsValid === true), preserving Polaris/other behavior.
     *   - hadTokenOnOpenRef: a returning user with a stale token would hit this
     *     the instant the modal mounts ("pops, flashes, closes" — can never
     *     re-auth). Only a token that appears WHILE the modal is open (a fresh
     *     social login) should auto-close.
     */
    useEffect(() => {
        if (fullToken && isOpen && !isEmailLoginRef.current && geoFieldsValid && !hadTokenOnOpenRef.current) {
            onLoginSuccess?.(successDataRef.current);
            onClose();
        }
    }, [fullToken, isOpen, onLoginSuccess, onClose, geoFieldsValid]);

    // Listen for social login completion message from popup
    useEffect(() => {
        const handleMessage = async (event: MessageEvent) => {
            /*
             * Do we trust the sender of this message?
             * (might be different from what we originally opened, for example).
             */
            if (event.origin !== import.meta.env.VITE_WEB_URL && !getAuthorizedExternalOrigin(event.origin)) return;
            // console.log("windowMessageListener", event.data);

            if (event.data?.type === "devourgo-social-registration" && event.data?.profileDataId) {
                const profileDataId = event.data.profileDataId;
                const email = event.data.email;
                void onProfileRegistration(profileDataId, email, referralCodeRef.current);
            }

            if (event.data?.type === "devourgo-social-login" && event.data?.authToken) {
                try {
                    /*
                     * onReauthorize updates the Redux fullToken; the useEffect on
                     * [fullToken, isOpen, geoFieldsValid] above then fires
                     * onLoginSuccess + onClose exactly once geo is valid. Calling
                     * onLoginSuccess directly here would bypass the geoFieldsValid
                     * guard for users on a geo-gated challenge who hit social login
                     * before picking a state, producing a 400 from /register.
                     */
                    await onReauthorize(event.data?.authToken, true);
                } catch (err) {
                    dispatch(await addError(err));
                }
            } else if (event.data?.type === "devourgo-social-login") {
                // Social login completed in popup - refresh will pick up the token
                window.location.reload();
            }
        };
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, []);

    // Reset state when modal closes
    useEffect(() => {
        if (!isOpen) {
            setLoginState(LoginState.INITIAL);
            setEmail("");
            setLoginRequestId("");
            setExtraEligibility({});
            setManualReferralCode("");
            setDeclaredCountry("");
            setPrizeEligibilityAck(false);
            setDeclaredState("");
            setForceEmailFallback(false);
            isEmailLoginRef.current = false;
            hadTokenOnOpenRef.current = false;
        }
    }, [isOpen]);

    /*
     * Country pre-fill: pre-select the Country picker from the async-fetched
     * `realmCountry` (a full country name, e.g. "United States"), mapped to its
     * ISO option via the loaded geography list. Fires when it arrives (a beat
     * after the modal opens) as long as the user hasn't picked yet
     * (declaredCountry empty), so it never overrides a manual choice. The user
     * still confirms it — eligibility country stays user-declared.
     */
    useEffect(() => {
        if (!isOpen || declaredCountry) return;
        const realmCountryName = realmCountry?.trim().toLowerCase();
        if (!realmCountryName || !geography?.countries) return;
        const match = geography.countries.find(c => c.name.trim().toLowerCase() === realmCountryName);
        if (match) setDeclaredCountry(match.code);
    }, [isOpen, realmCountry, geography, declaredCountry]);

    const handleSocialLogin = (provider: SocialProvider) => {
        if (!areButtonsActive) return;
        // Open in a popup window
        const currentUrl = new URL(window.location.href);
        const origin = currentUrl.origin;
        const includeState = Boolean(getAuthorizedExternalOrigin(origin));
        const url = new URL(provider.href);

        // Detect if running in Electron
        const isElectron = Boolean((window as any).electronAPI?.openExternal);

        if (isElectron) {
            // Set state to "electron" - backend will redirect to devourplay://
            url.searchParams.set("state", "electron");
            // Open in external browser
            (window as any).electronAPI.openExternal(url.toString());
        } else {
            if (includeState || currentUrl.searchParams.has("utm_source")) {
                url.searchParams.set("state", encodeURIComponent(currentUrl.toString()));
            }
            window.open(url.toString(), provider.id, "toolbars=0,width=500,height=700,left=200,top=100,scrollbars=1,resizable=1");
        }
    };

    const emailOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        setEmail(e.target.value);
    };

    const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!areButtonsActive || !validateEmail(email)) return;

        dispatch(incrementLoading());
        try {
            const loginRequest = await new VerificationsApi(getConfig()).createLoginCodeRequest({
                requestLoginCodeBody: {
                    email: email,
                    challengeSlug: resolvedChallengeSlug,
                    dropSlug,
                },
            });
            setLoginRequestId(loginRequest.loginCodeRequestId);
            setLoginState(LoginState.EMAIL_VERIFY);
        } catch (err) {
            dispatch(await addError(err));
        } finally {
            dispatch(decrementLoading());
        }
    };

    /*
     * Codeless "Continue" — signs the user in off the signed Realm embed with
     * no verification code (POST /challenges/:slug/realm-signin), stores the
     * session via onReauthorize (same path the email-code flow uses), then
     * fires onLoginSuccess so the parent posts terms + declared geo to
     * /register. A 422 means Realm has no email after all → fall back to the
     * standard email + verification-code flow rather than dead-ending.
     */
    const handleContinueCodeless = async () => {
        /*
         * Guard on the realm-signin mutation's own in-flight flag (React Query,
         * self-clearing) rather than the sticky isSubmittingRef. On SUCCESS the ref
         * was intentionally left true to swallow the iOS/WKWebView synthesized
         * re-tap, but it could survive into the NEXT modal open and dead-click
         * Continue until a reopen reset happened to fire — the intermittent dead
         * button. `isPending` is true only while the call is actually in flight and
         * clears itself the moment it settles, so there is no cross-session stuck
         * state; the in-flight network window already covers the ~120ms re-tap.
         */
        if (!areButtonsActive || realmSignin.isPending || dropRealmSignin.isPending || !codelessDoor) return;
        isEmailLoginRef.current = true; // the auth-close effect must not also fire
        dispatch(incrementLoading());
        try {
            const token = codelessDoor.kind === "drop"
                ? await dropRealmSignin.mutateAsync({ slug: codelessDoor.slug })
                : await realmSignin.mutateAsync({ slug: codelessDoor.slug });
            await onReauthorize(token, true);
            onLoginSuccess?.(successDataRef.current);
            onClose();
        } catch (err: any) {
            isEmailLoginRef.current = false;
            const status = err?.response?.status ?? err?.status;
            /*
             * Codeless couldn't complete — silently swap to the email +
             * verification-code path for ANY failure except a genuine disabled-
             * account 403 (which must surface its own error). No banner, no message:
             * the user shouldn't know codeless was even attempted. Covers 422 (no
             * Realm email), 401 (expired/void handshake), and 5xx / Users Core
             * Service outages — email + code needs no handshake, so it always works.
             * The upfront member-status decision means this is now a rare safety net,
             * not the primary path.
             */
            if (status === 403) {
                dispatch(await addError(err));
            } else {
                setForceEmailFallback(true);
            }
        } finally {
            dispatch(decrementLoading());
        }
    };

    const handleBackToEmail = () => {
        setLoginState(LoginState.INITIAL);
        setLoginRequestId("");
        setHasVerificationCodeError(false);
    };

    const handleSubmitVerificationCode = async (code: string): Promise<void> => {
        if (isSubmittingRef.current) {
            return;
        }
        isSubmittingRef.current = true;
        isEmailLoginRef.current = true; // Prevent useEffect from also closing modal

        dispatch(incrementLoading());
        try {
            const token = await new VerificationsApi(getConfig()).updateLoginCodeIdVerify({
                id: loginRequestId,
                loginCodeBody: {
                    verificationCode: code,
                    referralCode: referralCode,
                    challengeSlug: resolvedChallengeSlug,
                    dropSlug,
                    ...acquisition,
                },
            });
            if (token) {
                await onReauthorize(token, true);
                onLoginSuccess?.(successDataRef.current);
                onClose();
            }
        } catch (err) {
            /*
             * Only reset the guard on failure so the user can retry.
             * On success the modal closes — keeping isSubmittingRef=true blocks
             * the synthesized click event iOS/WKWebView fires ~120ms after touchend,
             * which would otherwise hit the verify endpoint a second time and show
             * a spurious "Verification code failed" error despite a successful login.
             */
            isSubmittingRef.current = false;
            setHasVerificationCodeError(true);
            dispatch(await addError(err));
        } finally {
            dispatch(decrementLoading());
        }
    };

    const handleRequestNewCode = async (): Promise<void> => {
        setHasVerificationCodeError(false);
        dispatch(incrementLoading());
        try {
            const loginRequest = await new VerificationsApi(getConfig()).createLoginCodeRequest({
                requestLoginCodeBody: {
                    email: email,
                    challengeSlug: resolvedChallengeSlug,
                    dropSlug,
                },
            });
            setLoginRequestId(loginRequest.loginCodeRequestId);
        } catch (err) {
            dispatch(await addError(err));
        } finally {
            dispatch(decrementLoading());
        }
    };

    /*
     * The two order-swappable form sections, extracted so `geoFirst` (RTTR) can
     * render geo-before-checkboxes without duplicating either block. Each is a
     * `false | element` (renders nothing when its gate is off). They read
     * component state directly; cheap to rebuild per render.
     */
    const attestationSection = !hideAttestation &&
        <div className="challenge-login-modal_checkboxes">
            {/*
              * Prize-eligibility acknowledgement renders FIRST on a surface that
              * declares US_ONLY prizes. Same copy for every registrant
              * (US-eligible, US-restricted-state, global alike) — the statement is
              * about the program rule, not the user's own residence. A surface
              * whose prizes are open never shows it, because there it is untrue.
              */}
            {needsPrizeAck &&
                <Checkbox
                    onToggle={() => setPrizeEligibilityAck(v => !v)}
                    checked={prizeEligibilityAck}
                    brandColor={challenge?.brandColor}
                    className="challenge-login-modal_checkbox"
                >
                    I understand only U.S. residents are eligible to win prizes and earn a spot in the U.S. Finals event.
                </Checkbox>
            }

            {/* Per-challenge admin-configured acknowledgements. */}
            {eligibilityCheckboxes.map(box =>
                <Checkbox
                    key={box.key}
                    onToggle={() => setExtraEligibility(prev => ({ ...prev, [box.key]: !prev[box.key] }))}
                    checked={extraEligibility[box.key] === true}
                    brandColor={challenge?.brandColor}
                    className="challenge-login-modal_checkbox"
                >
                    {box.label}
                </Checkbox>)}

            {/*
              * Combined age + terms attestation — one checkbox drives both isOver18
              * and agreedToTerms in lockstep; downstream gating reads the two
              * booleans independently.
              */}
            <Checkbox
                onToggle={() => {
                    const next = !(isOver18 && agreedToTerms);
                    setIsOver18(next);
                    setAgreedToTerms(next);
                }}
                checked={isOver18 && agreedToTerms}
                brandColor={challenge?.brandColor}
                className="challenge-login-modal_checkbox"
            >
                I am 18 or older and agree to the{" "}
                {/*
                  * Prefer challenge.termsUrl when set. RTTR points it at Termly's
                  * hosted page because our /terms-of-use renders the policy via a
                  * cross-origin Termly iframe the Realm webview blocks (header shows,
                  * body blank). Others fall back to the branded page.
                  *
                  * Protocol-guarded with the same test ChallengeTermsPage uses for this
                  * field — http(s) either case. The server already rejects anything else
                  * on write (validateClickableUrl), so this is the second lock, not the
                  * first, and it must not be stricter than the one that stores the value.
                  */}
                <a
                    href={challenge?.termsUrl && (/^https?:\/\//i).test(challenge.termsUrl.trim())
                        ? challenge.termsUrl.trim()
                        : "https://devourplay.gg/terms-of-use"}
                    target="_blank"
                    rel="noreferrer"
                >Terms of Use</a>.
            </Checkbox>
        </div>;
    const geoSection = hasGeoGate && !hideCountry &&
        <div className="challenge-login-modal_geo">
            <label className="challenge-login-modal_geo-label">
                Country
                {/*
                  * SearchableSelect — type-to-filter picker. Pins "US" at the top
                  * because RTTR is U.S.-eligible-only and the overwhelming majority
                  * of registrants are U.S. residents. When geoFirst (RTTR) the picker
                  * leads the form and is NOT locked behind the attestations; every
                  * other consumer keeps the attestations-first lock.
                  *
                  * While the async realmCountry fetch is in flight (and the user
                  * hasn't picked yet) show a field skeleton in place of the picker —
                  * the modal opens instantly off member-status and this one slow
                  * Minelab call fills the field a beat later. Reuses the platform
                  * `react-loading-skeleton` shimmer.
                  */}
                {realmCountryLoading && !declaredCountry
                    ? <div className="challenge-login-modal_field-skeleton react-loading-skeleton" aria-hidden="true" />
                    : <SearchableSelect
                        value={declaredCountry}
                        onChange={(next) => {
                            setDeclaredCountry(next);
                            setDeclaredState("");
                        }}
                        options={(geography?.countries ?? []).map(c => ({code: c.code, label: c.name}))}
                        pinCode="US"
                        placeholder="Select Country"
                        disabled={!geoFirst && !attestationsComplete}
                        ariaLabel="Country"
                        noKeyboard={geoFirst && isTouchDevice}
                    />
                }
            </label>

            {geoCountry === "US" &&
                <label className="challenge-login-modal_geo-label">
                    State
                    <SearchableSelect
                        value={declaredState}
                        onChange={setDeclaredState}
                        options={Object.entries(geography?.states ?? {}).map(([code, name]) => ({code, label: name}))}
                        placeholder="Select State"
                        disabled={!geoFirst && !attestationsComplete}
                        ariaLabel="State"
                        noKeyboard={geoFirst && isTouchDevice}
                    />
                </label>
            }

            {/*
              * Non-allowed country handling:
              *   - admitAllCountries=true: user joins for bragging rights. Show
              *     informational notice — does NOT gate the button.
              *   - admitAllCountries=false: hard 403. Modal blocks via geoFieldsValid.
              */}
            {isNonAllowedCountry && admitAllCountries && usOnlyPrizes &&
                <p className="challenge-login-modal_geo-soft" role="status">
                    You can join and earn points for bragging rights, but
                    <strong> prize-eligible features are reserved for U.S. residents</strong>.
                    {/*
                      * Protocol-guarded href — officialRulesUrl is admin-authored and
                      * lands here unvalidated; the https:// check blocks a javascript:
                      * URI. Same guard pattern as MinelabRttrHub's realm_home redirect.
                      */}
                    {eligibilityGeo?.officialRulesUrl?.startsWith("https://") &&
                        <> See the <a href={eligibilityGeo.officialRulesUrl} target="_blank" rel="noreferrer">Official Rules</a>.</>
                    }
                </p>
            }
            {isNonAllowedCountry && !admitAllCountries &&
                <p className="challenge-login-modal_geo-block" role="alert">
                    Sorry — this challenge is open only to U.S. residents.
                </p>
            }
            {isHardBlockedState &&
                <p className="challenge-login-modal_geo-block" role="alert">
                    Sorry — this challenge is not available in your state or territory.
                </p>
            }
            {/*
              * Soft-block state (e.g. NY/FL): informational copy only. No
              * acknowledgement checkbox — the universal eligibility checkbox in the
              * attestations covers the legal ack for every user, so a per-state ack
              * would be redundant.
              */}
            {isSoftBlockedState && !isHardBlockedState &&
                <p className="challenge-login-modal_geo-soft" role="status">
                    Your state of residence isn't eligible for prizes or the US Finals event, but you can still play to earn points and rank on the Global Leaderboard.
                    {/* Same admin-authored URL guard as the bragging-rights branch above. */}
                    {eligibilityGeo?.officialRulesUrl?.startsWith("https://") &&
                        <> See the <a href={eligibilityGeo.officialRulesUrl} target="_blank" rel="noreferrer">Official Rules</a>.</>
                    }
                </p>
            }
        </div>;
    const renderInitialState = () =>
        <>
            <div className="challenge-login-modal_content">
                <h6
                    className="challenge-login-modal_title"
                    style={challenge?.brandColor ? {
                        textShadow: `0px 0px 24px ${challenge.brandColor}, 0px 0px 8px ${challenge.brandColor}`,
                    } : undefined}
                >
                    {title}
                </h6>
                <p className="challenge-login-modal_description">{description}</p>
            </div>

            {/*
              * Pop-first (client 2026-07-28): the hub opens the modal IMMEDIATELY on
              * load and passes isLoading while challenge + member-status resolve, so
              * we show a loader here instead of making the pop wait on a slow external
              * member-status call. Prevents the social-button / form-shape flash
              * (member-status flips showCodeless + hideAttestation/hideCountry).
              *
              * geoFirst (RTTR) OPTS OUT of the full-form skeleton: its shape is a
              * known constant (Country/State + prize + terms + Continue), forced up
              * front via hasGeoGate/admitAllCountries, so we render the
              * real chrome (labels + checkboxes + Continue) immediately and skeleton
              * ONLY the Country field value while the slow realm-country call is in
              * flight (see realmCountryLoading below). Client ask 2026-07-28.
              */}
            {isLoading && !geoFirst
                ? <div className="challenge-login-modal_form-skeleton" role="status" aria-live="polite" aria-label="Loading sign-up form">
                    {/*
                      * Full-size skeleton of the form (two field boxes + two
                      * checkbox lines + a button) so the modal opens at ~full
                      * height and the real fields swap in place — it never grows
                      * from a tiny loader to the full form. Shimmer from the
                      * platform `.react-loading-skeleton` class.
                      */}
                    <div className="challenge-login-modal_field-skeleton react-loading-skeleton" />
                    <div className="challenge-login-modal_field-skeleton react-loading-skeleton" />
                    <div className="challenge-login-modal_block-skeleton react-loading-skeleton" />
                    <div className="challenge-login-modal_line-skeleton react-loading-skeleton" />
                    <div className="challenge-login-modal_button-skeleton react-loading-skeleton" />
                </div>
                : <>
                    {/*
                  * Section order: geoFirst=true (RTTR) renders Country/State first then
                  * the checkboxes; every other consumer keeps checkboxes-first,
                  * geo-second. areButtonsActive requires BOTH either way, so Continue
                  * enables only once geo is valid AND the boxes are ticked.
                  */}
                    {geoFirst
                        ? <>{geoSection}{attestationSection}</>
                        : <>{attestationSection}{geoSection}</>}

                    {/*
                      * Hold the method area (social / codeless / email) until the
                      * async member-status call resolves. geoFirst renders the geo +
                      * attestation immediately (known shape), but the method shape
                      * depends on canCodeless — rendering it before resolve flashes
                      * the social buttons + email form, then swaps to the codeless
                      * Continue. Skeleton a field + button so the height is reserved
                      * (no jump) while member-status resolves.
                      */}
                    {isLoading &&
                        <>
                            <div className="challenge-login-modal_field-skeleton react-loading-skeleton" aria-hidden="true" />
                            <div className="challenge-login-modal_button-skeleton react-loading-skeleton" aria-hidden="true" />
                        </>
                    }
                    {!isLoading && <>
                        {showManualReferralEntry && !showCodeless &&
                <div className="challenge-login-modal_referral">
                    <label className="challenge-login-modal_referral-label">
                        Referral code (optional)
                    </label>
                    <input
                        type="text"
                        placeholder={`e.g. ${challenge?.referralCodePrefix}12345`}
                        value={manualReferralCode}
                        onChange={(e) => setManualReferralCode(e.target.value.toUpperCase())}
                        className="challenge-login-modal_referral-input"
                        autoCapitalize="characters"
                        autoCorrect="off"
                        spellCheck={false}
                        maxLength={32}
                        onFocus={() => setIsReferralFocused(true)}
                        onBlur={() => setIsReferralFocused(false)}
                        style={isReferralFocused ? {
                            borderColor: "rgba(255, 255, 255, 0.4)",
                            outline: "none",
                        } : undefined}
                    />
                </div>
                        }
                        {visibleSocialProviders.length > 0 && !showCodeless &&
                <div className={`challenge-login-modal_social-buttons ${!areButtonsActive ? "challenge-login-modal_social-buttons--inactive" : ""}`}>
                    {visibleSocialProviders.map((provider) =>
                        <button
                            key={provider.id}
                            className="challenge-login-modal_social-button"
                            onClick={() => handleSocialLogin(provider)}
                            disabled={!areButtonsActive}
                            aria-label={`Login with ${provider.name}`}
                            style={areButtonsActive && challenge?.brandColor ? {
                                borderColor: challenge.brandColor,
                            } : undefined}
                        >
                            {provider.icon}
                            <span>{provider.name}</span>
                        </button>)}
                </div>
                        }

                        {showDividerBetweenMethods && !showCodeless &&
                <div className="challenge-login-modal_divider">
                    <span>or</span>
                </div>
                        }

                        {/*
              * Codeless "Continue" — the single-stage happy path. Replaces the
              * email field + social buttons when the signed embed + a Realm-
              * verified email let us authenticate without a code. Same brand
              * button styling as "Continue with Email"; gated on the same
              * attestation + geo validity (areButtonsActive).
              */}
                        {showCodeless &&
                (challenge?.brandColor
                    ? <button
                        type="button"
                        onClick={() => void handleContinueCodeless()}
                        disabled={!areButtonsActive}
                        className="challenge-login-modal_login-button challenge-login-modal_login-button--brand"
                        style={{
                            backgroundColor: challenge.brandColor,
                            borderColor: challenge.brandColor,
                            opacity: areButtonsActive ? 1 : 0.5,
                        }}
                    >
                        Continue
                    </button>
                    : <Button
                        variant="primary"
                        onClick={() => void handleContinueCodeless()}
                        {...{ type: "button" as const, disabled: !areButtonsActive }}
                    >
                        Continue
                    </Button>)
                        }

                        {showEmailMethod && !showCodeless &&
            <form onSubmit={handleEmailSubmit} className="challenge-login-modal_email-form">
                <div className="challenge-login-modal_email-input-wrapper">
                    <input
                        type="email"
                        placeholder="Enter your email address"
                        value={email}
                        onChange={emailOnChange}
                        disabled={!areButtonsActive}
                        className="challenge-login-modal_email-input"
                        onFocus={() => setIsEmailFocused(true)}
                        onBlur={() => setIsEmailFocused(false)}
                        onMouseEnter={() => setIsEmailHovered(true)}
                        onMouseLeave={() => setIsEmailHovered(false)}
                        style={isEmailFocused || isEmailHovered ? {
                            borderColor: "rgba(255, 255, 255, 0.4)",
                            outline: "none",
                        } : undefined}
                    />
                </div>
                {/*
                  * the legacy button's color prop only accepts the platform palette; when
                  * a brand hex is set we use a plain <button> so brandColor drives
                  * all states. Inline style is intentional — the legacy button has no custom-
                  * color API that covers hover/focus.
                  */}
                {challenge?.brandColor
                    ? <button
                        type="submit"
                        disabled={!areButtonsActive || !validateEmail(email)}
                        className="challenge-login-modal_login-button challenge-login-modal_login-button--brand"
                        style={{
                            backgroundColor: challenge.brandColor,
                            borderColor: challenge.brandColor,
                            opacity: areButtonsActive && validateEmail(email) ? 1 : 0.5,
                        }}
                    >
                        Continue with Email
                    </button>
                    : <Button variant="primary"
                        {...{type: "submit",
                            disabled: !areButtonsActive || !validateEmail(email)}}
                    >
                        Continue with Email
                    </Button>
                }
            </form>
                        }
                    </>
                    }
                </>
            }
        </>;
    const renderVerifyState = () =>
        <div className="challenge-login-modal_verify">
            <h6
                className="challenge-login-modal_title"
                style={challenge?.brandColor ? {
                    textShadow: `0px 0px 24px ${challenge.brandColor}, 0px 0px 8px ${challenge.brandColor}`,
                } : undefined}
            >
                Check your email
            </h6>
            <p className="challenge-login-modal_description">
                We sent a verification code to <strong>{email}</strong>
            </p>
            <LoginVerificationCode
                email={email}
                hasError={hasVerificationCodeError}
                onRequestNewCode={handleRequestNewCode}
                onSubmit={handleSubmitVerificationCode}
                brandColor={challenge?.brandColor}
            />
            <button
                className="challenge-login-modal_back-button"
                onClick={handleBackToEmail}
                type="button"
            >
                ← Back to sign in options
            </button>
        </div>;
    return (
        <Modal
            size="sm2"
            isOpen={isOpen}
            contentClassName="challenge-login-modal"
            containerClassName="challenge-login-modal_scrim"
            style={challenge?.brandColor ? { "--brand-color": challenge.brandColor } as React.CSSProperties : undefined}
        >
            {background?.cdnUrl &&
                <>
                    <div
                        className="challenge-login-modal_background"
                        style={{
                            backgroundImage: `url(${background.cdnUrl})`,
                        }}
                    />
                    <div className="challenge-login-modal_shader" />
                </>
            }
            <IoMdClose className="challenge-login-modal_close" onClick={onClose} />

            <ModalBody className="challenge-login-modal_body">
                {challenge?.brandLogo &&
                    <div className="challenge-login-modal_logo">
                        <AssetImage assetId={challenge.brandLogo} />
                    </div>
                }

                {errorMessage &&
                    <div className="challenge-login-modal_error-banner">
                        {errorMessage}
                    </div>
                }

                {loginState === LoginState.INITIAL && renderInitialState()}
                {loginState === LoginState.EMAIL_VERIFY && renderVerifyState()}
            </ModalBody>
        </Modal>
    );
};

export default ChallengeLoginModal;
